import { useState, useEffect } from "react";
import axios from "axios";
import { Camera } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const LazyArrivalPhoto = ({ inspectionId, corner, hasPhoto, onPhotoClick }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  
  const cornerLabels = {
    'FL': 'Front Left',
    'FR': 'Front Right', 
    'RL': 'Rear Left',
    'RR': 'Rear Right'
  };
  
  const loadPhoto = async () => {
    if (!hasPhoto || photoUrl || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/foh/inspection/${inspectionId}/arrival-photo/${corner}`);
      let url = res.data.photo_url;
      // Add data URI prefix if missing
      if (url && !url.startsWith('data:')) {
        url = `data:image/jpeg;base64,${url}`;
      }
      setPhotoUrl(url);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Load immediately when component mounts
  useEffect(() => {
    loadPhoto();
  }, []);
  
  if (!hasPhoto) {
    return (
      <div className="space-y-1">
        <div className="w-full h-32 bg-slate-100 rounded border border-slate-200 flex items-center justify-center">
          <span className="text-xs text-slate-400">No photo</span>
        </div>
        <p className="text-xs text-slate-500 text-center font-medium">{cornerLabels[corner] || corner}</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="space-y-1">
        <div className="w-full h-32 bg-slate-100 rounded border border-slate-200 flex items-center justify-center animate-pulse">
          <span className="text-xs text-slate-400">Loading...</span>
        </div>
        <p className="text-xs text-slate-500 text-center font-medium">{cornerLabels[corner] || corner}</p>
      </div>
    );
  }
  
  if (error || !photoUrl) {
    return (
      <div className="space-y-1">
        <div className="w-full h-32 bg-red-50 rounded border border-red-200 flex items-center justify-center">
          <span className="text-xs text-red-400">Failed to load</span>
        </div>
        <p className="text-xs text-slate-500 text-center font-medium">{cornerLabels[corner] || corner}</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <img 
        src={photoUrl}
        alt={`${corner} corner`}
        className="w-full h-32 object-cover rounded border border-slate-200 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400"
        onClick={() => onPhotoClick ? onPhotoClick(photoUrl) : null}
      />
      <p className="text-xs text-slate-500 text-center font-medium">{cornerLabels[corner] || corner}</p>
    </div>
  );
};

const LazyItemPhotos = ({ inspectionId, itemId, photoCount, onPhotoClick }) => {
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  const loadPhotos = async () => {
    if (photos || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/foh/inspection/${inspectionId}/photos/${itemId}`);
      // Add data URI prefix if missing for each photo
      const photoUrls = (res.data.photo_urls || []).map(url => {
        if (url && !url.startsWith('data:')) {
          return `data:image/jpeg;base64,${url}`;
        }
        return url;
      });
      
      // Also handle tester photo
      let testerPhotoUrl = res.data.tester_photo_url;
      if (testerPhotoUrl && !testerPhotoUrl.startsWith('data:')) {
        testerPhotoUrl = `data:image/jpeg;base64,${testerPhotoUrl}`;
      }
      
      setPhotos({ photo_urls: photoUrls, tester_photo_url: testerPhotoUrl });
    } catch (err) {
      console.error('Failed to load photos:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!expanded) {
    return (
      <button
        onClick={() => { setExpanded(true); loadPhotos(); }}
        className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
      >
        <Camera className="w-4 h-4" />
        View {photoCount} photo{photoCount > 1 ? 's' : ''}
      </button>
    );
  }
  
  if (loading) {
    return (
      <div className="flex gap-2 mt-2">
        {[...Array(Math.min(photoCount, 3))].map((_, idx) => (
          <div key={idx} className="w-16 h-16 bg-slate-100 rounded border border-slate-200 animate-pulse" />
        ))}
      </div>
    );
  }
  
  // Combine regular photos and tester photo
  const allPhotos = [...(photos?.photo_urls || [])];
  if (photos?.tester_photo_url) {
    allPhotos.push(photos.tester_photo_url);
  }
  
  if (!allPhotos.length) {
    return <p className="text-xs text-slate-400 mt-2">No photos available</p>;
  }
  
  return (
    <div className="flex gap-2 mt-2 flex-wrap">
      {/* Show tester photo first with special badge */}
      {photos?.tester_photo_url && (
        <div className="relative">
          <img 
            src={photos.tester_photo_url} 
            alt="Tester reading"
            className="w-16 h-16 object-cover rounded border-2 border-blue-400 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-500"
            onClick={() => onPhotoClick ? onPhotoClick(photos.tester_photo_url) : null}
          />
          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] px-1 rounded">TEST</span>
        </div>
      )}
      {/* Regular photos */}
      {photos?.photo_urls?.slice(0, 3).map((url, idx) => (
        <img 
          key={idx} 
          src={url} 
          alt={`Evidence ${idx + 1}`}
          className="w-16 h-16 object-cover rounded border border-slate-200 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400"
          onClick={() => onPhotoClick ? onPhotoClick(url) : null}
        />
      ))}
      {(photos?.photo_urls?.length || 0) > 3 && (
        <div 
          className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center text-xs text-slate-500 border border-slate-200 cursor-pointer hover:bg-slate-200"
          onClick={() => onPhotoClick ? onPhotoClick(photos.photo_urls[3]) : null}
        >
          +{photos.photo_urls.length - 3}
        </div>
      )}
    </div>
  );
};

export { LazyArrivalPhoto, LazyItemPhotos };
